// Who is signed in, as the app knows it: a module-level store any code can
// read (meStore.get()) or follow (meStore.subscribe), and useMe() for React.
// /api/me is asked once, on first use; refetch asks again. 'loading' until
// the first answer, null when signed out. The balance in it is for display
// only and is never written to the device store.
//
// Signing out here, or everywhere from here, also clears this device for
// whoever uses it next: the service worker's picture cache and the device
// store, which starts again as a visitor's with the demo projects. So does
// learning that the session ended somewhere else: an open socket hears it
// as close code 4001 (app/sync/sync.ts), and a device that was not connected
// then hears it as a 401 from /api/me the next time it asks. A 401 clears
// the device only when it holds a list's copy (the store names the list its
// move landed in); a visitor's own projects are never wiped by one.
//
// A suspended account is not signed out: /api/me answers its session with
// 403 `suspended`, and an open socket closes with 4003. Either way the
// device stops syncing (meStore reads null, so the session lets go of the
// list) and keeps everything it holds; pausedStore says so, for the banner
// (PausedBanner.tsx). The next answer that names the person clears it; one
// that names someone else finds the device holding another list, and the
// session clears it before anything moves (app/session.ts).
import { useEffect, useSyncExternalStore } from 'react';
import type { Me } from '@shared/api';
import { ApiFailure, api } from '../api';
import { SHARE_CACHE } from '@shared/share';
import { store, wipe } from '../store/store';
import { setupStore } from '../onboarding/setupStore';

export type MeState = Me | null | 'loading';

let state: MeState = 'loading';
let asked: Promise<void> | null = null;
const listeners = new Set<() => void>();

let paused = false;
const pausedListeners = new Set<() => void>();
function setPaused(next: boolean): void {
  if (paused === next) return;
  paused = next;
  for (const l of pausedListeners) l();
}

export const meStore = {
  get: (): MeState => state,
  set(next: Me | null): void {
    // Whoever is signed in now is not paused: an answer that names them is the server's word.
    if (next) setPaused(false);
    state = next;
    for (const l of listeners) l();
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },
};

/** Whether this account is suspended, as the server last said. The device keeps everything; only sync stops. */
export const pausedStore = {
  get: (): boolean => paused,
  subscribe(listener: () => void): () => void {
    pausedListeners.add(listener);
    return () => { pausedListeners.delete(listener); };
  },
};

export function usePaused(): boolean {
  return useSyncExternalStore(pausedStore.subscribe, pausedStore.get, pausedStore.get);
}

/** The account is suspended (a 403 `suspended`, or the socket's 4003): sync stops, nothing on the device is touched. */
export function accountPaused(): void {
  setPaused(true);
  meStore.set(null);
}

/** Whether this device holds a list's copy; a visitor's holds only its own projects, and the demo ones. */
async function holdsList(): Promise<boolean> {
  // The store is not read until it has opened; on a cold launch the 401 can come first.
  await store.ready.catch(() => undefined);
  // lastSeq for a device that synced before the store named its list.
  return store.listId() !== null || store.lastSeq() > 0;
}

/**
 * Asks /api/me again. A 401 means signed out, and on a device that holds a
 * list's copy, that the session ended elsewhere: the device is cleared. A
 * 403 `suspended` means the account is paused: signed out of sync, nothing
 * cleared. Offline or a server error keeps who we knew (a blip must not
 * sign anyone out of this device); on a first load with nothing known yet
 * it reads as a visitor until the next ask, which the session makes when
 * the network returns.
 */
export function refetchMe(): Promise<void> {
  asked = api<Me>('/api/me').then(
    (me) => meStore.set(me),
    async (err: unknown) => {
      if (!(err instanceof ApiFailure)) throw err;
      if (err.status === 403 && err.body.error === 'suspended') return accountPaused();
      if (err.status === 401) {
        if (await holdsList()) await signedOutHere();
        else meStore.set(null);
        return;
      }
      if (state === 'loading') meStore.set(null);
    },
  );
  return asked;
}

export function useMe(): { me: MeState; refetch: () => Promise<void> } {
  const me = useSyncExternalStore(meStore.subscribe, meStore.get, meStore.get);
  useEffect(() => {
    if (!asked) void refetchMe();
  }, []);
  return { me, refetch: refetchMe };
}

/** The service worker's runtime cache of pictures (sw.ts). */
export const MEDIA_CACHE = 'hd-media';

/**
 * Leaves nothing of the last person on this device: the service worker's
 * picture cache emptied (asked of the worker, and deleted from here too in
 * case no worker controls this page), then the device store wiped (the fresh
 * store is a visitor's, naming no list, and moves in again) and the setup
 * list forgotten. Who is signed in is left alone: signing out sets meStore
 * to null first (signedOutHere), and the session clears a device that holds
 * another person's list for whoever has just signed in. Never throws.
 */
export async function clearDevice(): Promise<void> {
  setPaused(false);
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_MEDIA' });
    if (typeof caches !== 'undefined') await Promise.all([caches.delete(MEDIA_CACHE), caches.delete(SHARE_CACHE)]);
  } catch {
    // No service worker or no Cache Storage here: nothing was cached.
  }
  await wipe().catch(() => undefined);
  setupStore.reset();
}

/** Signed out in meStore first, so the session stops sync at once and nothing more arrives from the list; then the device cleared. */
async function signedOutHere(): Promise<void> {
  meStore.set(null);
  await clearDevice();
}

/** Ends this device's session and clears the device. The store says signed out even if the request did not arrive. */
export async function signOut(): Promise<void> {
  try {
    await api('/api/auth/signout', { body: {} });
  } finally {
    await signedOutHere();
  }
}

/** The server ended this device's session (signed out elsewhere, or everywhere): clear the device, nothing to ask. */
export function sessionEnded(): Promise<void> {
  return signedOutHere();
}

/** Ends every session of this person, this one included, and clears this device. */
export async function signOutEverywhere(): Promise<void> {
  await api('/api/auth/signout-everywhere', { body: {} });
  await signedOutHere();
}

/** Test only: back to the first-load state. */
export function resetMeForTests(): void {
  state = 'loading';
  asked = null;
  paused = false;
}
